import { CaretLeftIcon, CaretRightIcon } from '@phosphor-icons/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router';

import { FALLBACK_LIMITS, useConfig } from '@entities/config/api';
import {
  SEARCH_SESSION_SORTS,
  TRACKER_STEPS,
  searchLimitContext,
  useCreateSearchSession,
  useDeleteSearchSession,
  usePatchSearchSession,
  useSearchSessions,
  type SearchLimitContext,
  type SearchSessionSort,
  type SearchSessionSummary,
} from '@entities/search-session';
import {
  DeleteSearchDialog,
  NewSearchForm,
  parseSearchesQuery,
  SearchLimitNotice,
  SearchSessionCard,
  STAGE_FILTERS,
  toSearchesApiQuery,
  toSearchesParams,
  type SearchesQuery,
  type StageFilter,
} from '@features/searches';
import { HERO_PROMPT_STORAGE_KEY, ROUTES } from '@shared/config/routes';
import { useAppShell } from '@shared/lib/appShell';
import { Button, Container, Select, Skeleton, StateBlock } from '@shared/ui';

import { OrdersSummary } from './OrdersSummary';

import styles from './SearchesPage.module.css';

/**
 * Запрос с главной живёт до создания подбора, а не до первого рендера (AC-01).
 *
 * Читается один раз и сразу удаляется: повторное создание запроса при возврате
 * на список было бы для пользователя необъяснимым.
 */
function takeHeroPrompt(): string | null {
  try {
    const value = sessionStorage.getItem(HERO_PROMPT_STORAGE_KEY);
    if (value !== null) sessionStorage.removeItem(HERO_PROMPT_STORAGE_KEY);
    return value && value.trim() !== '' ? value.trim() : null;
  } catch {
    // Приватный режим Safari: запрос просто не переживёт переход.
    return null;
  }
}

/** Вид кнопки раздела: выбранная — синий контур на синем тинте, остальные — `ghost`. */
function sectionButton(selected: boolean) {
  return {
    variant: selected ? ('outline-action' as const) : ('ghost' as const),
    className: selected ? styles.sectionActive : undefined,
    'aria-pressed': selected,
  };
}

/**
 * S-01 «Мои заказы» (FE-150, FE-152; MVP_PRD §20).
 *
 * Стартовая страница авторизованного пользователя и единственное место, где
 * живут все его подборы. Экран держит четыре вещи и ни одной сверх:
 *
 * 1. **список** с сортировкой и разделом архива — оба в URL, чтобы ссылка
 *    открывала ровно то, что видел пользователь;
 * 2. **создание** запроса. Новый запрос ничего не заменяет и не удаляет (AC-07);
 * 3. **управление**: переименование, архивирование, возврат из архива,
 *    удаление с подтверждением;
 * 4. **лимит**. При `409 SEARCH_LIMIT_REACHED` показывается список кандидатов
 *    на архивирование — и **ни один запрос не архивируется автоматически**
 *    (AC-08). Число в тексте берётся из ответа сервера.
 *
 * Ни одного решения о подборе здесь нет: агрегаты, статусы и названия считает
 * backend, страница только раскладывает их по карточкам.
 */
export function SearchesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const query = parseSearchesQuery(searchParams);

  const { data: config } = useConfig();
  const maxLength = (config?.limits ?? FALLBACK_LIMITS).chat_message_max_length;

  const listQuery = useSearchSessions(toSearchesApiQuery(query));
  const createSession = useCreateSearchSession();
  const patchSession = usePatchSearchSession();
  const deleteSession = useDeleteSearchSession();

  const [pendingDelete, setPendingDelete] = useState<SearchSessionSummary | null>(null);
  const [limit, setLimit] = useState<SearchLimitContext | null>(null);
  // Текст с главной. Если создать запрос не удалось (например, лимит), он
  // остаётся в форме: терять набранный пользователем текст нельзя.
  const [initialPrompt, setInitialPrompt] = useState('');
  const heroHandled = useRef(false);

  const apply = useCallback(
    (next: SearchesQuery) => {
      setSearchParams(toSearchesParams(next));
    },
    [setSearchParams],
  );

  const create = useCallback(
    (prompt: string | null) => {
      setLimit(null);
      createSession.mutate(
        { initialPrompt: prompt },
        {
          onSuccess: (state) => {
            // Диалог продолжается на странице запроса: у него свой адрес,
            // и ссылка на него воспроизводима (FE-151).
            void navigate(ROUTES.searchAssistant(state.session.id));
          },
          onError: (error) => {
            setLimit(searchLimitContext(error));
            // Текст остаётся в форме: пользователь его набрал, и терять его
            // из-за отказа сервера нельзя.
            if (prompt !== null) setInitialPrompt(prompt);
          },
        },
      );
    },
    [createSession, navigate],
  );

  /**
   * Запрос с главной (AC-01): текст сохраняется до входа, после входа под него
   * создаётся новый подбор и открывается диалог с этим текстом первым
   * сообщением. Один раз за монтирование — отсюда ref, а не состояние.
   */
  useEffect(() => {
    if (heroHandled.current) return;
    heroHandled.current = true;
    const prompt = takeHeroPrompt();
    if (prompt !== null) create(prompt);
  }, [create]);

  const archive = useCallback(
    (summary: { id: string; version: number }, archived: boolean) => {
      patchSession.mutate(
        { searchId: summary.id, version: summary.version, archived },
        {
          onSuccess: () => {
            setLimit(null);
          },
          onError: (error) => {
            // Возврат из архива тоже упирается в лимит: сервер отдаёт тот же
            // `409` со списком кандидатов.
            setLimit(searchLimitContext(error));
          },
        },
      );
    },
    [patchSession],
  );

  /**
   * Архивирование по кнопке из экрана лимита.
   *
   * Версия строки в кандидате не приходит: `409` отдаёт минимум данных для
   * экрана. Она берётся из уже загруженного списка, а если запроса на текущей
   * странице нет — список просто перечитывается, и пользователь архивирует
   * его карточкой. Слать `version` наугад нельзя: оптимистическая блокировка
   * ровно для того и существует.
   */
  const archiveCandidate = useCallback(
    (searchId: string) => {
      const summary = listQuery.data?.items.find((item) => item.session.id === searchId);
      if (!summary) {
        void listQuery.refetch();
        return;
      }
      archive({ id: searchId, version: summary.session.version }, true);
    },
    [archive, listQuery],
  );

  const page = listQuery.data;
  const items = page?.items ?? [];
  const totalPages = page?.total_pages ?? 0;
  const mutating = patchSession.isPending || deleteSession.isPending;
  const atLimit = page !== undefined && page.active_count >= page.limit;

  /**
   * Объяснение лимита показывается и **до** попытки создать запрос: форма
   * создания при исчерпанном лимите скрыта, и без текста экран выглядел бы
   * так, будто кнопку просто забыли. Кандидаты в этом случае не перечисляются —
   * они и так все на экране, каждый со своей кнопкой архивирования.
   */
  const limitNotice: SearchLimitContext | null =
    limit ??
    (atLimit && query.section === 'active' ? { limit: page.limit, archive_candidates: [] } : null);

  /**
   * Правый сайдбар `/app` — сводка и лимит заказов (DESIGN_SYSTEM §7).
   *
   * Стадию оболочке экран **не передаёт**: список — не заказ, и подсвечивать
   * ему шаг «какого-то» заказа было бы враньём о состоянии (FE-192).
   */
  useAppShell({
    context: page ? <OrdersSummary page={page} /> : null,
    contextLabel: t('searches.summary.label'),
  });

  return (
    <div className={styles.page}>
      <Container>
        <header className={styles.header}>
          <div>
            <h1 className={styles.title}>{t('searches.title')}</h1>
            <p className={styles.subtitle}>{t('searches.subtitle')}</p>
          </div>
          {page ? (
            <p className={styles.counter}>
              {t('searches.counter', { count: page.active_count, limit: page.limit })}
            </p>
          ) : null}
        </header>

        {/* Создание доступно всегда, кроме исчерпанного лимита: кнопка,
            которая заведомо вернёт 409, — это обещание несуществующего
            действия (MVP_PRD §2). Секция намеренно без `aria-label`: её
            единственное содержимое — форма с подписанной меткой, и второй
            такой же заголовок скринридер читал бы дважды. */}
        {query.section === 'active' && !atLimit ? (
          <section className={styles.createPanel}>
            <NewSearchForm
              key={initialPrompt}
              initialValue={initialPrompt}
              pending={createSession.isPending}
              maxLength={maxLength}
              onSubmit={create}
            />
          </section>
        ) : null}

        {limitNotice ? (
          <div className={styles.notice}>
            <SearchLimitNotice
              context={limitNotice}
              pending={mutating}
              onArchive={archiveCandidate}
            />
          </div>
        ) : null}

        <div className={styles.toolbar}>
          <Select
            label={t('searches.sort.label')}
            className={styles.sort}
            value={query.sort}
            onChange={(event) => {
              apply({ ...query, page: 1, sort: event.target.value as SearchSessionSort });
            }}
          >
            {SEARCH_SESSION_SORTS.map((option) => (
              <option key={option} value={option}>
                {t(`searches.sort.${option}`)}
              </option>
            ))}
          </Select>

          {/* Фильтр по стадии сгруппирован по шагам трекера: пользователь
              думает шагами, а не внутренними кодами стадий (MVP_PRD §33.1).
              В API уходит полный список стадий шага. */}
          <Select
            label={t('searches.stageFilter.label')}
            className={styles.sort}
            value={query.stage}
            onChange={(event) => {
              apply({ ...query, page: 1, stage: event.target.value as StageFilter });
            }}
          >
            {STAGE_FILTERS.map((option) => (
              <option key={option} value={option}>
                {option === 'all'
                  ? t('searches.stageFilter.all')
                  : t('searches.stageFilter.step', {
                      number: option,
                      name: t(`app.tracker.step.${TRACKER_STEPS[Number(option) - 1]?.key ?? ''}`),
                    })}
              </option>
            ))}
          </Select>

          {/* Раздел архива скрыт, пока архив пуст (MVP_PRD §20). Вкладки синие:
              выбранный раздел — состояние, а не главное действие, и красной
              заливкой не красится (DESIGN_SYSTEM §2.3, §5.1). */}
          {page && (page.archived_count > 0 || query.section === 'archived') ? (
            <div className={styles.sections}>
              <Button
                {...sectionButton(query.section === 'active')}
                onClick={() => {
                  apply({ ...query, page: 1, section: 'active' });
                }}
              >
                {t('searches.section.active')}
              </Button>
              <Button
                {...sectionButton(query.section === 'archived')}
                onClick={() => {
                  apply({ ...query, page: 1, section: 'archived' });
                }}
              >
                {t('searches.section.archived', { count: page.archived_count })}
              </Button>
            </div>
          ) : null}
        </div>

        {listQuery.isPending ? (
          <>
            <p className="visually-hidden" role="status">
              {t('searches.loading')}
            </p>
            <div className={styles.loading} aria-busy="true">
              {Array.from({ length: 3 }, (_, index) => (
                <Skeleton key={index} height="12rem" radius="var(--radius-card)" />
              ))}
            </div>
          </>
        ) : null}

        {listQuery.isError ? (
          <div className={styles.stateWrap}>
            <StateBlock
              tone="error"
              title={t('searches.error.title')}
              description={t('searches.error.text')}
              action={
                <Button
                  variant="secondary"
                  onClick={() => {
                    void listQuery.refetch();
                  }}
                >
                  {t('error.retry')}
                </Button>
              }
            />
          </div>
        ) : null}

        {/* Три разных пустых состояния: «заказов ещё нет», «архив пуст» и «на
            этом шаге ничего нет». Склеивать их нельзя — ответы и следующие
            шаги разные (S-11 п. 4). */}
        {listQuery.isSuccess && items.length === 0 ? (
          <div className={styles.stateWrap}>
            {query.stage !== 'all' ? (
              <StateBlock
                title={t('searches.empty.stage.title')}
                description={t('searches.empty.stage.text')}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      apply({ ...query, page: 1, stage: 'all' });
                    }}
                  >
                    {t('searches.stageFilter.reset')}
                  </Button>
                }
              />
            ) : query.section === 'archived' ? (
              <StateBlock
                title={t('searches.empty.archive.title')}
                description={t('searches.empty.archive.text')}
                action={
                  <Button
                    variant="secondary"
                    onClick={() => {
                      apply({ ...query, page: 1, section: 'active' });
                    }}
                  >
                    {t('searches.section.active')}
                  </Button>
                }
              />
            ) : (
              <StateBlock
                title={t('searches.empty.title')}
                description={t('searches.empty.text')}
              />
            )}
          </div>
        ) : null}

        {items.length > 0 ? (
          <ul className={styles.list} aria-busy={listQuery.isFetching}>
            {items.map((summary) => (
              <li key={summary.session.id}>
                <SearchSessionCard
                  summary={summary}
                  pending={mutating}
                  onRename={(title) => {
                    patchSession.mutate({
                      searchId: summary.session.id,
                      version: summary.session.version,
                      title,
                    });
                  }}
                  onArchive={() => {
                    archive({ id: summary.session.id, version: summary.session.version }, true);
                  }}
                  onRestore={() => {
                    archive({ id: summary.session.id, version: summary.session.version }, false);
                  }}
                  onDelete={() => {
                    setPendingDelete(summary);
                  }}
                />
              </li>
            ))}
          </ul>
        ) : null}

        {totalPages > 1 ? (
          <nav className={styles.pagination} aria-label={t('catalog.pagination.label')}>
            <Button
              variant="secondary"
              disabled={query.page <= 1}
              onClick={() => {
                apply({ ...query, page: query.page - 1 });
              }}
            >
              <CaretLeftIcon size={16} weight="bold" aria-hidden="true" />
              {t('catalog.pagination.previous')}
            </Button>
            <p className={`${styles.pageStatus} tabular`}>
              {t('catalog.pagination.status', { page: query.page, total: totalPages })}
            </p>
            <Button
              variant="secondary"
              disabled={query.page >= totalPages}
              iconEnd={<CaretRightIcon size={16} weight="bold" />}
              onClick={() => {
                apply({ ...query, page: query.page + 1 });
              }}
            >
              {t('catalog.pagination.next')}
            </Button>
          </nav>
        ) : null}
      </Container>

      <DeleteSearchDialog
        open={pendingDelete !== null}
        title={pendingDelete?.session.title ?? null}
        pending={deleteSession.isPending}
        onCancel={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (!pendingDelete) return;
          deleteSession.mutate(pendingDelete.session.id, {
            onSuccess: () => {
              setPendingDelete(null);
            },
          });
        }}
      />
    </div>
  );
}
